import { CustomerCheckoutData } from '../types/admin';
import { customersApi } from './api';

// API-based Customer Service
class CustomerService {
  // Normalize status from API (uppercase) to frontend (lowercase)
  private normalizeStatus(status: string): CustomerCheckoutData['status'] {
    const normalized = status?.toLowerCase() as CustomerCheckoutData['status'];
    const valid: CustomerCheckoutData['status'][] = ['info_filled', 'order_placed', 'payment_completed', 'cancelled'];
    return valid.includes(normalized) ? normalized : 'info_filled';
  }

  // Normalize a customer record from API to frontend format
  private normalizeCustomer(customer: any): CustomerCheckoutData {
    return {
      ...customer,
      status: this.normalizeStatus(customer.status),
      // Ensure attendeeNames is always an array
      attendeeNames: Array.isArray(customer.attendeeNames) ? customer.attendeeNames : [],
      // Ensure orderData.total is a number
      orderData: customer.orderData ? {
        ...customer.orderData,
        total: typeof customer.orderData.total === 'string' 
          ? parseFloat(customer.orderData.total) || 0 
          : customer.orderData.total || 0,
        quantity: typeof customer.orderData.quantity === 'string'
          ? parseInt(customer.orderData.quantity) || 1
          : customer.orderData.quantity || 1
      } : undefined
    };
  }

  // Get all customer data (admin-specific)
  async getAllCustomerData(adminId?: string, companyName?: string): Promise<CustomerCheckoutData[]> {
    try {
      const query: any = {};
      if (companyName) {
        query.companyName = companyName;
      }
      const response = await customersApi.getAllCustomers(query);
      // API returns { success: true, data: [...], message: "..." }
      let rawData: any[] = [];
      if (Array.isArray(response)) {
        rawData = response;
      } else if (response.data && Array.isArray(response.data)) {
        rawData = response.data;
      } else if (response.customers && Array.isArray(response.customers)) {
        rawData = response.customers;
      }
      // Normalize all customer records
      return rawData.map(c => this.normalizeCustomer(c));
    } catch (error) {
      console.error('Error loading customer data:', error);
      return [];
    }
  }

  // Get customer data by event ID (admin-specific)
  async getCustomerDataByEvent(eventId: string, adminId?: string): Promise<CustomerCheckoutData[]> {
    const allData = await this.getAllCustomerData(adminId);
    return allData.filter(customer => customer.eventId === eventId);
  }

  // Save customer checkout information (admin-specific)
  async saveCustomerData(customerData: Omit<CustomerCheckoutData, 'id' | 'submittedAt'>, adminId?: string): Promise<CustomerCheckoutData> {
    try {
      // Convert to API format with uppercase status
      const apiData: any = {
        ...customerData,
        status: customerData.status.toUpperCase()
      };
      
      const response = await customersApi.saveCustomerData(apiData);
      return response.customer;
    } catch (error: any) {
      console.error('Error saving customer data:', error);
      throw new Error(error.response?.data?.message || 'Failed to save customer data');
    }
  }

  // Update customer data status
  async updateCustomerStatus(customerId: string, status: CustomerCheckoutData['status'], orderData?: CustomerCheckoutData['orderData']): Promise<void> {
    try {
      // Convert status to uppercase for API
      const apiStatus = status.toUpperCase();
      await customersApi.updateCustomerStatus(customerId, apiStatus, orderData);
    } catch (error) {
      console.error('Error updating customer status:', error);
      throw error;
    }
  }

  // Delete customer data
  async deleteCustomerData(customerId: string): Promise<void> {
    try {
      await customersApi.deleteCustomerData(customerId);
    } catch (error) {
      console.error('Error deleting customer data:', error);
      throw error;
    }
  }

  // Get customer statistics
  async getCustomerStats(eventId?: string) {
    try {
      const allData = eventId ? await this.getCustomerDataByEvent(eventId) : await this.getAllCustomerData();
      
      const totalCustomers = allData.length;
      const completedOrders = allData.filter(c => c.isCompleted).length;
      const infoOnlyCustomers = allData.filter(c => c.status === 'info_filled').length;
      
      const conversionRate = totalCustomers > 0 ? (completedOrders / totalCustomers) * 100 : 0;
      
      const totalRevenue = allData
        .filter(c => c.orderData && c.isCompleted)
        .reduce((sum, c) => sum + (c.orderData?.total || 0), 0);
      
      const averageOrderValue = completedOrders > 0 ? totalRevenue / completedOrders : 0;

      return {
        totalCustomers,
        completedOrders,
        infoOnlyCustomers,
        conversionRate: Math.round(conversionRate * 100) / 100,
        totalRevenue,
        averageOrderValue: Math.round(averageOrderValue * 100) / 100
      };
    } catch (error) {
      console.error('Error getting customer stats:', error);
      return {
        totalCustomers: 0,
        completedOrders: 0,
        infoOnlyCustomers: 0,
        conversionRate: 0,
        totalRevenue: 0,
        averageOrderValue: 0
      };
    }
  }
}

export const customerService = new CustomerService();
